<template>
  <div class="p-0">
    <router-link to="/leave/admin/legal-documents" class="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900 mb-5">
      <i class="fa-solid fa-arrow-left mr-2"></i>
      Volver a documentos legales
    </router-link>

    <header class="mb-5">
      <p class="breadcrumb">Licencias y permisos / Administracion / Documentos legales</p>
      <h1 class="text-2xl font-bold text-gray-900">{{ isEdit ? 'Editar documento legal' : 'Nuevo documento legal' }}</h1>
    </header>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <form v-else class="panel" @submit.prevent="save">
      <div class="panel-heading">
        <i class="fa-regular fa-file-lines text-indigo-800"></i>
        <h2 class="font-bold text-gray-900">Datos del documento</h2>
      </div>

      <div v-if="error" class="alert-error">{{ error }}</div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="label">Codigo *</label>
          <input v-model.trim="form.code" required class="field" placeholder="Ej: DEC_4118_97" />
        </div>
        <div>
          <label class="label">Tipo *</label>
          <input v-model.trim="form.document_type" required class="field" placeholder="Ej: Decreto" />
        </div>
        <div>
          <label class="label">Numero *</label>
          <input v-model.trim="form.number" required class="field" placeholder="Ej: 4118" />
        </div>
        <div>
          <label class="label">Anio</label>
          <input v-model.trim="form.year" class="field" placeholder="Ej: 1997" />
        </div>
        <div class="md:col-span-2">
          <label class="label">Titulo *</label>
          <input v-model.trim="form.title" required class="field" placeholder="Titulo descriptivo del documento legal" />
        </div>
        <div class="md:col-span-2">
          <label class="label">Descripcion</label>
          <textarea v-model.trim="form.description" rows="5" class="field" placeholder="Descripcion del alcance y objetivos del documento"></textarea>
        </div>
      </div>

      <div class="mt-5 flex flex-wrap gap-5">
        <label class="check-line">
          <input v-model="form.is_main" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
          Documento principal
        </label>
        <label class="check-line">
          <input v-model="form.active" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
          Activo
        </label>
      </div>

      <div class="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <router-link to="/leave/admin/legal-documents" class="secondary-button">Cancelar</router-link>
        <button type="submit" :disabled="saving" class="primary-button disabled:opacity-50">
          {{ saving ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </form>

    <aside class="mt-5 info-box">
      <i class="fa-solid fa-circle-info text-indigo-700 mt-1"></i>
      <div>
        <h3 class="text-sm font-bold text-gray-900">Instrucciones de registro</h3>
        <p class="mt-1 text-sm text-gray-600">
          Los documentos marcados como activo quedan disponibles para asociar articulos. Para desactivar un documento, edita el registro y desmarca Activo.
        </p>
      </div>
    </aside>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import { legalDocumentsApi } from '@/service/leave.service';
import { unwrapData } from '@/utils/leave';

const emptyForm = () => ({
  code: '',
  document_type: '',
  number: '',
  year: '',
  title: '',
  description: '',
  is_main: false,
  active: true,
});

export default {
  name: 'LeaveAdminLegalDocumentFormView',
  components: { Loading },
  data() {
    return {
      form: emptyForm(),
      loading: false,
      saving: false,
      error: '',
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id);
    },
  },
  mounted() {
    if (this.isEdit) {
      this.fetchDocument();
    }
  },
  methods: {
    async fetchDocument() {
      this.loading = true;
      this.error = '';
      try {
        const response = await legalDocumentsApi.get(this.$route.params.id);
        const document = unwrapData(response) || {};
        this.form = { ...emptyForm(), ...document };
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo cargar el documento.';
      } finally {
        this.loading = false;
      }
    },
    payload() {
      return {
        code: this.form.code,
        document_type: this.form.document_type,
        number: this.form.number,
        year: this.form.year || null,
        title: this.form.title,
        description: this.form.description || null,
        is_main: Boolean(this.form.is_main),
        active: Boolean(this.form.active),
      };
    },
    async save() {
      this.saving = true;
      this.error = '';
      try {
        if (this.isEdit) {
          await legalDocumentsApi.update(this.$route.params.id, this.payload());
        } else {
          await legalDocumentsApi.create(this.payload());
        }
        this.$router.push('/leave/admin/legal-documents');
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo guardar el documento legal.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.breadcrumb {
  @apply text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2;
}
.panel {
  @apply bg-white border border-gray-200 rounded-xl shadow-sm p-5;
}
.panel-heading {
  @apply flex items-center gap-3 border-b border-gray-100 pb-4 mb-5;
}
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.label {
  @apply block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1;
}
.check-line {
  @apply inline-flex items-center gap-2 text-sm font-semibold text-gray-700;
}
.primary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800 shadow-sm;
}
.secondary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50;
}
.alert-error {
  @apply bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4;
}
.info-box {
  @apply bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-4 flex gap-3;
}
</style>
